import { Component, TemplateFunction } from '@ribajs/core'
import { ScrollEventsService, getScrollPosition } from '@ribajs/extras'
import type { PredictiveSearch } from './predictive-search.component.js'
import type { HeaderMenu } from './header-menu.component.js'

interface Scope {
  // METHODS
  hide: StickyHeaderComponent['_hide']
  show: StickyHeaderComponent['_show']
  full: StickyHeaderComponent['_full']
  slim: StickyHeaderComponent['_slim']

  // OPTIONS
  topOffset: number
}

enum WindowPosition {
  TOP,
  INSIDE_HEADER,
  UNDER_HEADER,
}

/**
 * A sticky-header element, converted from the original theme code to typescript
 */
export class StickyHeaderComponent extends Component {
  public static tagName = 'sb-sticky-header'

  shopifySectionHeader?: HTMLElement
  predictiveSearch?: PredictiveSearch
  menuHeader?: HeaderMenu
  /** Element with class `.header` which contains the icons and search bar */
  headerToolsWrapper?: HTMLDivElement

  // Add additional scroll events like `scrolldown`, `scrollup` and `scrollended` to `window`.
  touchEventService = new ScrollEventsService(window)

  onResize = this._onResize.bind(this)
  onScrollUp = this._onScrollUp.bind(this)
  onScrollDown = this._onScrollDown.bind(this)
  onScrollEnd = this._onScrollEnd.bind(this)

  static get observedAttributes(): string[] {
    return ['top-offset']
  }

  public scope: Scope = {
    hide: this._hide.bind(this),
    show: this._show.bind(this),
    full: this._full.bind(this),
    slim: this._slim.bind(this),
    topOffset: 34,
  }

  constructor() {
    super()
  }

  connectedCallback() {
    super.connectedCallback()
    this.init(StickyHeaderComponent.observedAttributes)

    this.shopifySectionHeader =
      (document.getElementsByClassName('section-header')[0] as HTMLElement) ||
      undefined
    this.predictiveSearch =
      this.querySelector<PredictiveSearch>('predictive-search') || undefined
    this.headerToolsWrapper =
      this.querySelector<HTMLDivElement>('.header') || undefined

    if (!this.shopifySectionHeader || !this.headerToolsWrapper) {
      throw new Error('Header elements not found!')
    }

    this.addEventListeners()
    this.onScrollEnd()
  }

  protected async afterBind() {
    await super.afterBind()
    this.classList.add(`${StickyHeaderComponent.tagName}-ready`)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.removeEventListeners()
  }

  addEventListeners() {
    window.addEventListener('scrolldown', this.onScrollDown, { passive: true })
    window.addEventListener('scrollup', this.onScrollUp, { passive: true })
    window.addEventListener('scrollended', this.onScrollEnd, { passive: true })
    window.addEventListener('resize', this.onResize, { passive: true })
  }

  removeEventListeners() {
    this.removeEventListener('scrolldown', this.onScrollDown)
    window.removeEventListener('scrollup', this.onScrollUp)
    window.removeEventListener('scrollended', this.onScrollEnd)
    window.removeEventListener('resize', this.onResize)
  }

  protected _onResize() {
    this._onScrollEnd()
    if (this.menuHeader?.isOpen() || this.predictiveSearch?.isOpen) {
      this.scope.show()
    }
  }

  protected _onScrollUp() {
    const pos = this.getPosition()

    switch (pos) {
      case WindowPosition.UNDER_HEADER:
        this.scope.slim()
        this.scope.hide()
        break
      default:
        break
    }
  }

  protected _onScrollDown() {
    this.scope.show()

    const pos = this.getPosition()

    switch (pos) {
      case WindowPosition.TOP:
      case WindowPosition.INSIDE_HEADER:
        this.scope.full()
        break
    }
  }

  protected _onScrollEnd() {
    const pos = this.getPosition()

    switch (pos) {
      case WindowPosition.TOP:
        this.scope.show()
        this.scope.full()
        break
      case WindowPosition.INSIDE_HEADER:
        this.scope.full()
        break
      case WindowPosition.UNDER_HEADER:
        this.scope.slim()
        break
      default:
        break
    }

    this.setMenuBottomPosition()
  }

  protected getPosition() {
    const pos = getScrollPosition(window)
    let position: WindowPosition
    if (pos.y <= this.scope.topOffset) {
      position = WindowPosition.TOP
    } else if (pos.y <= this.offsetHeight + this.scope.topOffset) {
      position = WindowPosition.INSIDE_HEADER
    } else {
      position = WindowPosition.UNDER_HEADER
    }

    return position
  }

  /** Hide the header */
  protected _hide() {
    if (this.menuHeader?.isOpen() || this.predictiveSearch?.isOpen) {
      return
    }

    if (
      this.shopifySectionHeader &&
      !this.shopifySectionHeader.classList.contains(
        'shopify-section-header-hidden',
      )
    ) {
      this.shopifySectionHeader.classList.add('shopify-section-header-hidden')
    }
  }

  /** Show the header */
  protected _show() {
    if (
      this.shopifySectionHeader &&
      this.shopifySectionHeader.classList.contains(
        'shopify-section-header-hidden',
      )
    ) {
      this.shopifySectionHeader?.classList.remove(
        'shopify-section-header-hidden',
      )
    }
  }

  /** Switch to full mode (with navigation) */
  protected _full() {
    if (
      this.shopifySectionHeader &&
      !this.shopifySectionHeader.classList.contains(
        'shopify-section-header-full',
      )
    ) {
      this.shopifySectionHeader.classList.remove('shopify-section-header-slim')
      this.shopifySectionHeader.classList.add('shopify-section-header-full')
    }
  }

  /** Switch to slim mode (no navigation) */
  protected _slim() {
    if (
      this.shopifySectionHeader &&
      !this.shopifySectionHeader.classList.contains(
        'shopify-section-header-slim',
      )
    ) {
      this.shopifySectionHeader.classList.add('shopify-section-header-slim')
      this.shopifySectionHeader.classList.remove('shopify-section-header-full')
    }
  }

  protected setMenuBottomPosition() {
    window.requestAnimationFrame(() => {
      const bottom = Math.floor(
        this.headerToolsWrapper?.getBoundingClientRect().bottom ||
          this.getBoundingClientRect().bottom ||
          0,
      )
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${bottom}px`,
      )
    })
  }

  protected template(): ReturnType<TemplateFunction> {
    return null
  }
}
